import { api } from '@/services/api';
import debounce from 'lodash/debounce';
import { subGames, affiliates, affiliate, zipCodeLookup } from '@/services/api/queries';
import { createOrder, createGameSubscription, createAffiliate } from '@/services/api/mutations';
import { __DEV__ } from '@/constants';

const GameTypes = {
    PARENT: 'parent',
    CURRENT: 'current',
};

function createApiPlugin(store) {
    return new ApiPlugin(store);
}

class ApiPlugin {
    constructor(store) {
        this.store = store;
    }

    install(Vue) {
        Vue.prototype.$api = this;
    }

    _parentHeaders() {
        return { headers: { Authorization: 'Bearer ' + this.store.getters.parentToken } };
    }

    _currentHeaders() {
        return { headers: { Authorization: 'Bearer ' + this.store.getters.token } };
    }

    getConfig(onBehalfOf) {
        return onBehalfOf === GameTypes.PARENT ? this._parentHeaders() : this._currentHeaders();
    }

    post(query, variables, onBehalfOf) {
        let config = this.getConfig(onBehalfOf);
        return api.post('', { query, variables }, config);
    }

    getAffiliate(affiliateId, onBehalfOf = GameTypes.CURRENT) {
        let variables = { affiliateId };
        return this.post(affiliate, variables, onBehalfOf)
            .then(({ data }) => {
                return data?.data?.affiliate;
            })
            .catch(error => console.log(error));
    }

    getAffiliates({ gameId, filters, sorting, pagination }, onBehalfOf = GameTypes.CURRENT) {
        let variables = { gameId, filters, sorting, pagination: pagination };
        return this.post(affiliates, variables, onBehalfOf)
            .then(({ data }) => {
                if (data.data?.affiliates?.errors?.length > 0) throw data?.data?.affiliates?.errors;
                return data?.data?.affiliates?.items;
            })
            .catch(error => console.log(error));
    }

    createAffiliate(formInput, onBehalfOf = GameTypes.CURRENT) {
        return this.post(createAffiliate, { input: formInput }, onBehalfOf).then(({ data }) => {
            if (!data?.data?.createAffiliate?.ok) {
                throw data?.data?.createAffiliate?.error;
            }

            if (data?.data?.createAffiliate?.ok && data?.data?.createAffiliate) {
                return data.data.createAffiliate;
            }
        });
    }

    getSubGames({ gameId, filters, sorting, pagination }, onBehalfOf = GameTypes.CURRENT) {
        let variables = { gameId, filters, sorting, pagination: pagination };
        return this.post(subGames, variables, onBehalfOf)
            .then(({ data }) => {
                if (data.data?.subGames?.errors?.length > 0) throw data?.data?.subGames?.errors;
                return data?.data?.subGames?.items;
            })
            .catch(error => console.log(error));
    }

    createOrder(formInput, onBehalfOf = GameTypes.CURRENT) {
        return this.post(createOrder, { input: formInput }, onBehalfOf).then(({ data }) => {
            if (!data?.data?.createOrder?.ok) {
                throw data?.data?.createOrder?.error;
            }

            if (data?.data?.createOrder?.ok && data?.data?.createOrder) {
                return data.data.createOrder;
            }
        });
    }

    createGameSubscription(formInput, onBehalfOf = GameTypes.CURRENT) {
        return this.post(createGameSubscription, { input: formInput }, onBehalfOf).then(({ data }) => {
            if (!data?.data?.createGameSubscription?.ok) {
                throw data?.data?.createGameSubscription?.error;
            }
            if (data?.data?.createGameSubscription?.ok && data?.data?.createGameSubscription) {
                return data.data.createGameSubscription;
            }
        });
    }

    zipCodeLookup(gameId, zipCode, houseNumber, onBehalfOf = GameTypes.CURRENT) {
        const _zipCode = zipCode.replace(/\s+/g, '');
        let variables = { gameId, zipCode: _zipCode, houseNumber };
        return this.post(zipCodeLookup, variables, onBehalfOf)
            .then(({ data }) => {
                const { ok, result, error } = data?.data?.zipCodeLookup;

                if (!data?.data?.zipCodeLookup?.ok) {
                    throw error;
                }

                return { ok, result };
            })
            .catch(error => {
                if (__DEV__) {
                    console.log(error);
                }

                return { ok: false };
            });
    }

    zipCodeLookupDebounced(gameId, zipCode, houseNumber, debounceTime = 1000, onBehalfOf = GameTypes.CURRENT) {
        let func = debounce(this.zipCodeLookup.bind(this), debounceTime, { leading: true, trailing: true });
        return func(gameId, zipCode, houseNumber, onBehalfOf);
    }
}

export default { ApiPlugin, createApiPlugin };
export { ApiPlugin, createApiPlugin };
